import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Hash, Calendar, Sparkles, MessageCircle, Loader2, ArrowRight, Lock } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import CTABand from "@/components/CTABand";
import { Reveal } from "@/lib/motion";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";
import { BRAND, IMAGES } from "@/lib/content";
import { toast } from "sonner";

const waBase = `https://wa.me/${BRAND.whatsapp}?text=`;

function ResultRow({ label, value }) {
  return (
    <div className="flex justify-between gap-4 py-3 border-b border-white/10">
      <span className="text-[#94A3B8]">{label}</span>
      <span className="text-white text-right font-medium">{value}</span>
    </div>
  );
}

// Trail a reading off with an ellipsis so the full version stays gated.
function teaser(text, words = 4) {
  const parts = String(text || "").replace(/[.\u3002]\s*$/, "").trim().split(/\s+/).filter(Boolean);
  const shown = parts.slice(0, words).join(" ");
  return `${shown} …`;
}

// Locked teaser panel with a "Connect with Komel on WhatsApp" CTA.
function LockedReading({ wa, testId, items }) {
  return (
    <div className="mt-6 relative overflow-hidden rounded-2xl border border-gold/25 p-6" data-testid="numerology-locked">
      <div className="space-y-2 blur-[6px] select-none pointer-events-none" aria-hidden="true">
        {items.map((l) => (
          <div key={l} className="flex justify-between gap-4 py-2 border-b border-white/10">
            <span className="text-[#94A3B8]">{l}</span>
            <span className="text-white">••••••••••</span>
          </div>
        ))}
      </div>
      <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-5 bg-[#060A17]/55 backdrop-blur-[2px]">
        <span className="grid place-items-center h-11 w-11 rounded-full bg-gold/15 ring-1 ring-gold/30 text-gold mb-3"><Lock size={18} /></span>
        <p className="text-white font-medium">Your full reading is ready</p>
        <p className="text-[#94A3B8] text-sm mt-1 mb-4 max-w-sm">Unlock your complete analysis, lucky colours, lucky days &amp; personalised guidance with Komel.</p>
        <a href={wa} target="_blank" rel="noreferrer" data-testid={testId} className="inline-flex items-center gap-2 rounded-full bg-[#25D366] px-6 py-3.5 font-medium text-white transition-transform hover:-translate-y-0.5">
          <MessageCircle size={18} /> Connect with Komel on WhatsApp
        </a>
      </div>
    </div>
  );
}

function NameCalculator() {
  const [name, setName] = useState("");
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return toast.error("Please enter a name");
    setLoading(true);
    try {
      const r = await api.post("/numerology/name", { name });
      setRes(r.data);
    } catch (e) {
      toast.error("Could not calculate. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const wa = `${waBase}${encodeURIComponent(`Hi Komel, my name numerology result: Lucky Number ${res?.lucky_number} (${res?.title}). I'd like my complete detailed reading.`)}`;

  return (
    <div>
      <form onSubmit={submit} className="flex flex-col sm:flex-row gap-3">
        <Input data-testid="name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Enter full name" className="bg-white/5 border-white/15 h-12 text-base text-white placeholder:text-[#64748B]" />
        <button data-testid="name-submit" type="submit" disabled={loading} className="inline-flex items-center justify-center gap-2 rounded-xl bg-primary-green px-6 h-12 font-medium text-white transition-colors hover:bg-[#059669] disabled:opacity-60">
          {loading ? <Loader2 className="animate-spin" size={18} /> : <>Calculate <ArrowRight size={16} /></>}
        </button>
      </form>
      <AnimatePresence>
        {res && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mt-8" data-testid="name-result">
            <div className="flex items-center gap-5 mb-6">
              <div className="grid place-items-center h-20 w-20 rounded-2xl bg-gold/15 ring-1 ring-gold/30">
                <span className="font-display text-5xl text-gold">{res.lucky_number}</span>
              </div>
              <div>
                <p className="text-[#94A3B8] text-sm">Your Lucky Number & Archetype</p>
                <h4 className="font-display text-3xl">{res.title}</h4>
              </div>
            </div>
            <ResultRow label="Personality" value={res.personality} />
            <ResultRow label="Today's Guidance" value={res.daily_guidance} />
            <div className="flex justify-between gap-4 py-3 border-b border-white/10">
              <span className="text-[#94A3B8]">Ideal Career</span>
              <span className="text-white text-right font-medium">{teaser(res.career, 4)}</span>
            </div>
            <LockedReading wa={wa} testId="name-whatsapp" items={["Number Analysis", "Lucky Colours", "Lucky Day", "Personalised Guidance"]} />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function DOBCalculator() {
  const [dob, setDob] = useState({ day: "", month: "", year: "" });
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const day = parseInt(dob.day), month = parseInt(dob.month), year = parseInt(dob.year);
    if (!day || !month || !year) return toast.error("Please enter a valid date");
    setLoading(true);
    try {
      const r = await api.post("/numerology/dob", { day, month, year });
      setRes(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Invalid date of birth");
    } finally {
      setLoading(false);
    }
  };

  const wa = `${waBase}${encodeURIComponent(`Hi Komel, my date-of-birth reading: Life Path ${res?.life_path_number}, Destiny ${res?.destiny_number}. I'd like my full personalised consultation.`)}`;

  return (
    <div>
      <form onSubmit={submit} className="grid grid-cols-3 gap-3">
        <Input data-testid="dob-day" value={dob.day} onChange={(e) => setDob({ ...dob, day: e.target.value })} placeholder="DD" type="number" className="bg-white/5 border-white/15 h-12 text-base text-white placeholder:text-[#64748B]" />
        <Input data-testid="dob-month" value={dob.month} onChange={(e) => setDob({ ...dob, month: e.target.value })} placeholder="MM" type="number" className="bg-white/5 border-white/15 h-12 text-base text-white placeholder:text-[#64748B]" />
        <Input data-testid="dob-year" value={dob.year} onChange={(e) => setDob({ ...dob, year: e.target.value })} placeholder="YYYY" type="number" className="bg-white/5 border-white/15 h-12 text-base text-white placeholder:text-[#64748B]" />
        <button data-testid="dob-submit" type="submit" disabled={loading} className="col-span-3 inline-flex items-center justify-center gap-2 rounded-xl bg-primary-green px-6 h-12 font-medium text-white transition-colors hover:bg-[#059669] disabled:opacity-60">
          {loading ? <Loader2 className="animate-spin" size={18} /> : <>Reveal My Numbers <ArrowRight size={16} /></>}
        </button>
      </form>
      <AnimatePresence>
        {res && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mt-8" data-testid="dob-result">
            <div className="grid grid-cols-2 gap-4 mb-6">
              {[["Destiny Number", res.destiny_number], ["Life Path Number", res.life_path_number]].map(([l, v]) => (
                <div key={l} className="rounded-2xl border border-gold/20 bg-gold/[0.05] p-5 text-center">
                  <div className="font-display text-5xl text-gold">{v}</div>
                  <div className="text-xs text-[#94A3B8] mt-1">{l}</div>
                </div>
              ))}
            </div>
            <ResultRow label="Life Path Archetype" value={res.life_path_title} />
            <ResultRow label="Personality" value={res.personality} />
            <ResultRow label="Today's Guidance" value={res.daily_guidance} />
            <div className="flex justify-between gap-4 py-3 border-b border-white/10">
              <span className="text-[#94A3B8]">Career Suggestions</span>
              <span className="text-white text-right font-medium">{teaser(res.career, 4)}</span>
            </div>
            <LockedReading wa={wa} testId="dob-whatsapp" items={["Relationship Insights", "Lucky Days", "Lucky Colours", "Personalised Guidance"]} />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function Numerology() {
  return (
    <div className="relative z-10">
      <PageHeader
        overline="Interactive Tools"
        titleLines={["Numerology", <span key="2" className="text-gold italic">Calculators</span>]}
        subtitle="Decode the hidden vibration of your name and birth date — instantly."
        image={IMAGES.compass}
      />
      <section className="py-10 md:py-16">
        <div className="mx-auto max-w-7xl px-6 md:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4 lg:sticky lg:top-28 self-start">
            <Reveal>
              <p className="overline text-gold mb-5">Why Numerology</p>
              <h2 className="font-display text-3xl md:text-4xl leading-tight">Numbers carry energy. So does your name.</h2>
              <p className="mt-5 text-[#94A3B8] leading-relaxed">
                Every letter and date resonates at a specific frequency. Aligning your name and choices with your core numbers can unlock clarity, confidence and success.
              </p>
              <ul className="mt-8 space-y-4">
                {[["Name Calculator", "Lucky number, personality & good/bad analysis"], ["Date of Birth Reading", "Destiny & life-path numbers, career and relationship insights"]].map(([t, d]) => (
                  <li key={t} className="flex gap-3">
                    <Sparkles size={18} className="text-gold shrink-0 mt-1" />
                    <div><div className="text-white font-medium">{t}</div><div className="text-sm text-[#94A3B8]">{d}</div></div>
                  </li>
                ))}
              </ul>
            </Reveal>
          </div>
          <div className="lg:col-span-8">
            <Reveal>
              <div className="glass rounded-3xl p-7 md:p-9">
                <Tabs defaultValue="name">
                  <TabsList className="bg-white/5 border border-white/10 mb-8">
                    <TabsTrigger value="name" data-testid="tab-name" className="data-[state=active]:bg-primary-green data-[state=active]:text-white gap-2"><Hash size={16} /> Name Calculator</TabsTrigger>
                    <TabsTrigger value="dob" data-testid="tab-dob" className="data-[state=active]:bg-primary-green data-[state=active]:text-white gap-2"><Calendar size={16} /> Date of Birth</TabsTrigger>
                  </TabsList>
                  <TabsContent value="name"><NameCalculator /></TabsContent>
                  <TabsContent value="dob"><DOBCalculator /></TabsContent>
                </Tabs>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
      <CTABand />
    </div>
  );
}
